import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Lets a page render into chrome that its ancestors own.
 *
 * Two slots:
 *
 *  • **header** - the app header. The shell used to carry a breadcrumb naming
 *    the section and the page repeated that name as its `<h1>` 87px later; two
 *    rows of chrome to say "Settings" twice, on a screen that may never scroll
 *    (D-15), where every pixel of height comes out of the data.
 *
 *  • **tabBar** - the right-hand end of a `LinkTabs` row. A settings panel owns
 *    its own Save button, but the tab row belongs to the layout above it. Given
 *    a slot, the two share one row instead of stacking.
 *
 * Portals rather than context state on purpose: passing content up through a
 * provider means the page's render sets provider state, which re-renders the
 * page, which sets it again. A portal has no such loop and is always current.
 * The slot is null for one render (the owner mounts first), which costs a
 * single extra paint.
 */
interface Slots {
  header: HTMLElement | null;
  tabBar: HTMLElement | null;
  setTabBar: (el: HTMLElement | null) => void;
}

const SlotsContext = createContext<Slots | null>(null);

export function PageChromeProvider({
  children,
}: {
  children: (setHeader: (el: HTMLDivElement | null) => void) => ReactNode;
}) {
  const [header, setHeader] = useState<HTMLElement | null>(null);
  const [tabBar, setTabBarEl] = useState<HTMLElement | null>(null);

  // Stable identity so a consumer's ref callback does not re-fire every render.
  const setTabBar = useCallback((el: HTMLElement | null) => setTabBarEl(el), []);
  const value = useMemo<Slots>(
    () => ({ header, tabBar, setTabBar }),
    [header, tabBar, setTabBar],
  );

  return <SlotsContext.Provider value={value}>{children(setHeader)}</SlotsContext.Provider>;
}

/** Renders `children` into the app header, or nowhere if there is no shell. */
export function HeaderPortal({ children }: { children: ReactNode }) {
  const slot = useContext(SlotsContext)?.header ?? null;
  if (!slot) return null;
  return createPortal(children, slot);
}

/** True when a shell header is available to portal into. */
export function useHasHeaderSlot(): boolean {
  return (useContext(SlotsContext)?.header ?? null) !== null;
}

/**
 * Registers the element that panel actions should render into. Called by the
 * component that owns the tab row.
 */
export function useTabBarSlotRef(): (el: HTMLElement | null) => void {
  const ctx = useContext(SlotsContext);
  const noop = useCallback(() => undefined, []);
  return ctx?.setTabBar ?? noop;
}

/**
 * Renders `children` at the end of the tab row. Falls back to rendering in
 * place when there is no tab row, so a panel used outside a tabbed layout
 * still shows its actions.
 */
export function TabBarPortal({ children }: { children: ReactNode }) {
  const slot = useContext(SlotsContext)?.tabBar ?? null;
  if (!slot) return <>{children}</>;
  return createPortal(children, slot);
}
